import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { PodCrossCheck } from '../api/client'
import { useIsAdmin } from '../auth/AuthContext'
import { EmailReport } from '../components/EmailReport'
import { OutboundInfoButton } from '../components/OutboundInfoButton'
import { PodCrossCheckPanel } from '../components/PodCrossCheck'
import { formatElapsed, useElapsed } from '../hooks/useElapsed'
import { theme } from '../theme'
import { fmtTime } from '../utils/datetime'

/**
 * Batch report — aggregate statistics plus every item.
 *
 * The statistics engine goes to some trouble to avoid quoting a figure it cannot support:
 * percentiles are suppressed below their minimum sample size, verdicts carry their
 * denominator, and combined-window fallback items are excluded from the per-leg populations.
 * None of that is worth anything unless this page SHOWS it, so a suppressed percentile
 * renders as `n/a` with its reason rather than being hidden, and every verdict prints the
 * arithmetic behind it.
 */

const POLL_MS = 5000

type Timings = {
  count: number
  min: number | null
  max: number | null
  mean: number | null
  median: number | null
  stddev: number | null
  p90: number | null
  p95: number | null
  p99: number | null
  p90_suppressed?: boolean
  p95_suppressed?: boolean
  p99_suppressed?: boolean
  p90_min_n?: number
  p95_min_n?: number
  p99_min_n?: number
  suppression_note?: string
}

type Sla = {
  verdict: string
  reason: string
  threshold_minutes: number | null
  evaluable: number
  met: number
  breached: number
  excluded_no_threshold: number
  excluded_incomplete: number
  /** How many items this verdict is WAITING ON — undecided with their own window still open.
   *  A SUBSET of `excluded_incomplete`, not a fourth exclusion bucket: both are undecided, but
   *  these will answer and the rest are measurements we will not get. Optional because a
   *  STORED snapshot from schema 6 or earlier never asked the question, and its absence must
   *  read as "unknown" rather than as zero. */
  awaiting_decision?: number
  pass_pct_required: number
  pass_pct_actual: number | null
}

type Perspective = { window: string; timings: Timings; sla: Sla }

type Stats = {
  total_items: number
  percentile_method: string
  percentile_minimum_n: Record<string, number>
  fallback_excluded_from_per_leg: { count: number; run_tokens: string[]; note: string }
  perspectives: Record<string, Perspective>
  /* FINISH-LINE A4 items 11 and 12 — `Perspective.threshold_note` and the whole `quality`
     object are gone from this type because this page no longer renders any of them, and a
     hand-written response type that declares a field nothing reads invites the paragraph back.
     The API still returns every one of them, unchanged: the emailed HTML report
     (services/batch_report.py) and the Slack card (services/slack/render.py) render them, and
     tests/test_quality_denominator_and_disclosure.py still asserts them there. Per-item quality
     arrives on `Item`, below, and is still shown in the Items table. */
  status_counts: Record<string, number>
}

type Item = {
  id: string
  batch_seq: number
  run_token: string
  status: string
  outbound_seconds: number | null
  inbound_seconds: number | null
  combined_seconds: number | null
  outbound_sla_breached: boolean | null
  inbound_sla_breached: boolean | null
  quality_result: string | null
  quality_score: number | null
  send_error: string | null
  quality_detail: string | null
}

function secs(v: number | null) {
  if (v === null || v === undefined) return '—'
  return v < 10 ? `${v.toFixed(2)}s` : `${v.toFixed(1)}s`
}

// `undecided` is AMBER and not grey, because it is not the same non-answer as `n/a`. An
// operator watched a batch print a green PASS while its faxes were still arriving — 3 of 50 in,
// 3/3 = 100% against a 95% requirement — so the engine now withholds a verdict until every item
// is in or past its own window. Grey would say "nothing to measure here"; amber says "not yet",
// which is the true state and the one worth coming back to. The tooltip carries the count,
// because a withheld verdict must say what it is waiting on rather than going quiet.
const VERDICT_COLOUR: Record<string, string> = {
  pass: '#4caf50',
  fail: '#f44336',
  undecided: '#ffa726',
}

function Verdict({ v, awaiting }: { v: string; awaiting?: number }) {
  const colour = VERDICT_COLOUR[v] ?? '#9aa0a6'
  const title =
    v === 'undecided'
      ? `No terminal verdict yet${awaiting ? `: ${awaiting} item(s) still in flight` : ''}. ` +
        'An SLA verdict is terminal only once every item is in or its own timer has expired, ' +
        'so this is neither a pass nor a fail — a rate over the items that arrived first would ' +
        'judge the batch on its fastest faxes.'
      : v === 'n/a'
        ? 'Not evaluated: nothing here is under an SLA, or nothing evaluable was measured. ' +
          'That is not a pass, and no verdict is coming.'
        : undefined
  return (
    <span
      style={{ color: colour, fontWeight: 700, fontSize: '0.9rem', cursor: title ? 'help' : undefined }}
      title={title}
    >
      {v === 'n/a' ? 'N/A' : v.toUpperCase()}
    </span>
  )
}

/** A percentile cell that says WHY it is absent rather than showing a blank or a max. */
function Pct({ t, p }: { t: Timings; p: 90 | 95 | 99 }) {
  const val = t[`p${p}` as 'p90'] as number | null
  const suppressed = t[`p${p}_suppressed` as 'p90_suppressed']
  const minN = t[`p${p}_min_n` as 'p90_min_n']
  if (suppressed) {
    return (
      <span
        style={{ color: '#777', fontStyle: 'italic' }}
        title={`p${p} needs at least ${minN} samples; this population has ${t.count}. Showing the maximum here instead would be indistinguishable from a real percentile.`}
      >
        n/a (n={t.count})
      </span>
    )
  }
  return <span>{secs(val)}</span>
}

function PerspectiveCard({ name, p }: { name: string; p: Perspective }) {
  const t = p.timings
  const s = p.sla
  return (
    <div style={{ background: theme.bgAlt, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0.9rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong style={{ textTransform: 'capitalize' }}>{name}</strong>
        <Verdict v={s.verdict} awaiting={s.awaiting_decision} />
      </div>
      <div style={{ fontSize: '0.7rem', color: '#666', fontFamily: 'monospace' }}>{p.window}</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.2rem 0.6rem', fontSize: '0.78rem', marginTop: '0.7rem' }}>
        <span style={{ color: '#9aa0a6' }}>n</span><span>{t.count}</span>
        <span style={{ color: '#9aa0a6' }}>median</span><span>{secs(t.median)}</span>
        <span style={{ color: '#9aa0a6' }}>mean</span><span>{secs(t.mean)}</span>
        <span style={{ color: '#9aa0a6' }}>min / max</span><span>{secs(t.min)} / {secs(t.max)}</span>
        <span style={{ color: '#9aa0a6' }}>stddev</span>
        <span>{t.stddev === null ? <span style={{ color: '#777', fontStyle: 'italic' }}>n/a (n={t.count})</span> : secs(t.stddev)}</span>
        <span style={{ color: '#9aa0a6' }}>p90</span><span><Pct t={t} p={90} /></span>
        <span style={{ color: '#9aa0a6' }}>p95</span><span><Pct t={t} p={95} /></span>
        <span style={{ color: '#9aa0a6' }}>p99</span><span><Pct t={t} p={99} /></span>
      </div>

      {/* The denominator, always. "93%" means nothing without knowing 93 of what. */}
      <div style={{ marginTop: '0.7rem', paddingTop: '0.6rem', borderTop: `1px solid ${theme.border}`, fontSize: '0.75rem' }}>
        <div style={{ color: '#c8c8c8' }}>{s.reason}</div>
        {(s.excluded_incomplete > 0 || s.excluded_no_threshold > 0) && (
          <div style={{ color: '#ffa726', marginTop: '0.3rem' }}>
            Excluded from the denominator:{' '}
            {s.excluded_incomplete > 0 && `${s.excluded_incomplete} with an incomplete leg`}
            {s.excluded_incomplete > 0 && s.excluded_no_threshold > 0 && ', '}
            {s.excluded_no_threshold > 0 && `${s.excluded_no_threshold} with no threshold configured`}
          </div>
        )}
        {/* Named SEPARATELY from the exclusions above, because it is a different fact about a
            different future: an excluded item is a measurement we do not have, and this one is
            a measurement we do not have YET. Reading the two as one sentence is how a batch
            came to print a pass over the faxes that happened to arrive first. */}
        {(s.awaiting_decision ?? 0) > 0 && (
          <div style={{ color: '#ffa726', marginTop: '0.3rem' }}>
            {s.awaiting_decision} of those are STILL IN FLIGHT — undecided with their own window
            open — so no terminal verdict exists for this perspective yet.
          </div>
        )}
        {/* FINISH-LINE A4 item 11 — the combined-budget paragraph (`threshold_note`, set only
            on the COMBINED perspective) is no longer rendered here. Every rule it described is
            unchanged in the engine, in backend/services/batch_stats: the combined budget is
            still DERIVED per job as the sum of the configured components and never stored, it
            is still an ADDITIONAL constraint and never forgiveness, and a component that blew
            its own threshold still fails the item whatever the combined window says
            (`_item_met_sla`). The field is still computed and still served — the email report
            renders it — so this is a surface removal, not a policy change. */}
      </div>
    </div>
  )
}

/**
 * The live elapsed timer (G2.1-G2.12).
 *
 * THE STATE TOKEN IS CONSUMED, NEVER DERIVED. `timer_state` is computed once on the server
 * (backend/services/batch_stats.batch_timer_state) and the Slack surface consumes the same
 * token. Re-deriving the rule here — "red if any item breached, green if the pass rate is
 * met" — is how two surfaces come to give an operator different answers about the same
 * batch, which this codebase has shipped more than once. This component maps the token to a
 * label, an icon and a colour, and computes nothing.
 *
 * COLOUR IS NEVER THE ONLY CHANNEL (G2.11). Every state carries a text label and an icon, so
 * the indicator is legible to a colour-blind operator and in a screenshot printed in
 * greyscale. The `aria-live` region announces a state change to a screen reader without
 * announcing every tick of the clock.
 */
const TIMER_PRESENTATION: Record<
  string,
  { label: string; icon: string; cls: string; hint: string }
> = {
  dispatching: {
    label: 'Dispatching',
    icon: '◔',
    cls: 'bt-open',
    hint: 'The ledger is durable and the leader is picking the batch up. Sends begin within one poll cycle.',
  },
  running: {
    label: 'Running',
    icon: '◔',
    cls: 'bt-open',
    hint: 'Faxes are in flight. Nothing has breached its SLA and nothing has failed.',
  },
  passed: {
    label: 'Passed',
    icon: '✔',
    cls: 'bt-passed',
    hint: 'Every item finished, every measurable item met its SLA, and the batch cleared its pass-rate requirement.',
  },
  breached: {
    label: 'Breached',
    icon: '✕',
    cls: 'bt-breached',
    hint: 'At least one item breached its SLA or failed. The clock keeps running; a later success does not undo a breach.',
  },
  exceeded_24h: {
    label: 'Exceeded 24h',
    icon: '⏹',
    cls: 'bt-exceeded',
    hint: 'The timer stops at 24 hours. Items still open past that point will not be matched to an inbound fax after the configured matching window closes.',
  },
}

/** Two palettes, because no single colour clears WCAG AA against both a near-black and a
 *  near-white background — 4.5:1 on #121212 and on #ffffff are mutually exclusive. The
 *  light-scheme block is what makes the AA claim true in both themes rather than only in the
 *  one the app currently ships. */
const TIMER_CSS = `
.bt-open { color: #e8eaed; }
.bt-passed { color: #6fdc8c; }
.bt-breached { color: #ff8a80; }
.bt-exceeded { color: #cfcfcf; }
@media (prefers-color-scheme: light) {
  .bt-open { color: #202124; }
  .bt-passed { color: #146c2e; }
  .bt-breached { color: #b3261e; }
  .bt-exceeded { color: #3c4043; }
}
`

function BatchTimer({ batch }: { batch: any }) {
  const state: string = batch.timer_state || 'running'
  const closed = state === 'passed' || state === 'exceeded_24h'
  const elapsed = useElapsed({
    startIso: batch.t_created,
    // The server decides when the measurement is closed and hands back the last arrival; the
    // page does not guess it from item statuses.
    endIso: closed ? batch.t_last_received || batch.t_completed || null : null,
    serverNowIso: batch.server_now,
  })
  const shown = elapsed === null ? batch.elapsed_seconds ?? null : elapsed
  const p = TIMER_PRESENTATION[state] || {
    label: `Unknown state (${state})`,
    icon: '?',
    cls: 'bt-exceeded',
    hint: 'The server reported a timer state this page does not recognise. It is shown verbatim rather than guessed at.',
  }
  return (
    <div
      style={{
        background: theme.bgAlt,
        border: `1px solid ${theme.border}`,
        borderRadius: 6,
        padding: '0.7rem 0.9rem',
        marginBottom: '1rem',
      }}
    >
      <style>{TIMER_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.7rem', flexWrap: 'wrap' }}>
        <span
          className={p.cls}
          style={{ fontSize: '1.9rem', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
        >
          {formatElapsed(shown)}
        </span>
        <span className={p.cls} style={{ fontSize: '0.95rem', fontWeight: 700 }} aria-live="polite">
          <span aria-hidden="true" style={{ marginRight: '0.35rem' }}>{p.icon}</span>
          {p.label}
        </span>
        <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>
          since launch {batch.t_created ? `(${fmtTime(batch.t_created)})` : ''}
          {closed && batch.t_last_received ? ' · stopped at the last fax received' : ''}
        </span>
      </div>
      <div style={{ fontSize: '0.74rem', color: theme.textMuted, marginTop: '0.35rem' }}>
        {p.hint}
      </div>
      {/* FINISH-LINE A4 item 10 — the timer-colour paragraph is no longer rendered here.
          The RULE is unchanged and still stated once, server-side, in
          backend/services/batch_stats.TIMER_COLOUR_SENTENCE: the colour comes from the
          stored per-item SLA verdicts, computed from the provider's own timestamps and never
          from how long this timer has been running. The state token is still consumed, never
          re-derived (see this component's docstring), so nothing about the colour changed —
          only the sentence describing it was removed from the surface. It is deliberately NOT
          restated here: two copies of one rule is the drift this file's docstring warns about. */}
    </div>
  )
}

export default function BatchReport() {
  const { id } = useParams<{ id: string }>()
  const isAdmin = useIsAdmin()
  const [batch, setBatch] = useState<any>(null)
  const [items, setItems] = useState<Item[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  // THE POD CROSS-CHECK (B2.16). Published as `pod_cross_check`, NOT `pod_attribution` --
  // that name is already a provider CAPABILITY BOOLEAN on this API, so the two would be one
  // name with two types across two endpoints, which the compiler cannot catch.
  const [podCheck, setPodCheck] = useState<PodCrossCheck | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/batches/${id}`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setBatch(d.batch)
      setItems(d.items || [])
      setStats(d.stats || null)
      setPodCheck(d.pod_cross_check || null)
      setErr(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [id])

  useEffect(() => {
    load()
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [load])

  if (err) {
    return (
      <div>
        <Link to="/tactical" style={{ color: theme.primary, fontSize: '0.8rem' }}>← Tactical E2E</Link>
        <div style={{ background: '#3a1e1e', border: '1px solid #6a2a2a', borderRadius: 6, padding: '1rem', marginTop: '1rem' }}>
          <p style={{ margin: 0, color: '#ef9a9a' }}>{err}</p>
        </div>
      </div>
    )
  }
  if (!batch) return <p style={{ color: '#9aa0a6' }}>Loading…</p>

  const open = batch.queued + batch.in_flight

  // ARRIVING BEFORE DISPATCH HAS BEGUN.
  //
  // Launch returns as soon as the item ledger is durable; dispatch is deliberately deferred
  // to the leader's batch monitor, which adopts the batch within one 5s cycle. Since G1 sends
  // the operator straight here from the launch button, this is the state the page opens in
  // EVERY time — so it must read as "starting", not as a broken or empty report.
  //
  // The predicate is the full ledger being untouched: every item still queued, none in flight,
  // and no item rows returned yet. Once the monitor adopts the batch even one item leaves
  // `queued`, so this cannot linger over a batch that is genuinely running.
  const dispatchPending = items.length === 0 && batch.in_flight === 0 && batch.queued === batch.size

  return (
    <div>
      <Link to="/tactical" style={{ color: theme.primary, fontSize: '0.8rem' }}>← Tactical E2E</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: '0.5rem 0 0.2rem' }}>
            Batch of {batch.size} — {batch.job_name || batch.job_id?.slice(0, 8)}
          </h2>
          <div style={{ fontSize: '0.78rem', color: '#9aa0a6', marginBottom: '1rem' }}>
            {batch.status} · {batch.terminal} finished, {batch.in_flight} in flight, {batch.queued} queued
            · pass requires {batch.sla_pass_pct}% · stagger {batch.stagger_ms}ms
          </div>
        </div>
        {/* The HTML download and the emailed copy come from ONE renderer, so they cannot disagree
            about the same batch. Both read the stored snapshot rather than recomputing. */}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
          <a
            href={`/api/batches/${id}/report.html`}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', background: theme.bgAlt, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4, textDecoration: 'none' }}
          >
            Download HTML
          </a>
          <a
            href={`/api/batches/${id}/report.csv`}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem', background: theme.bgAlt, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 4, textDecoration: 'none' }}
            title="One row per fax, for re-analysis in a spreadsheet"
          >
            CSV
          </a>
          {isAdmin && <EmailReport batchId={id!} />}
        </div>
      </div>

      <BatchTimer batch={batch} />

      {/* ABOVE THE STATISTICS, DELIBERATELY. If this batch measured a pod other than the one
          it names, that fact qualifies every figure below it -- so an operator must meet it
          before the numbers, not in a footnote after them. It renders nothing at all for a
          public-path batch, which has no target to disagree with. */}
      <PodCrossCheckPanel check={podCheck} />

      {dispatchPending && (
        <div style={{ background: theme.bgElevated, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0.6rem 0.8rem', fontSize: '0.78rem', color: '#64b5f6', marginBottom: '1rem' }}>
          <strong>Dispatch starting</strong> — all {batch.size} faxes are queued and the ledger
          is durable. Sending is handed to the leader's batch monitor rather than to the request
          that launched the batch, because only the leader runs the scheduler that polls each
          send for its outbound completion. The monitor adopts the batch within one 5-second
          cycle, then the stagger spreads the sends. Nothing is wrong and nothing is lost — this
          page refreshes itself every {POLL_MS / 1000} seconds.
        </div>
      )}

      {open > 0 && !dispatchPending && (
        <div style={{ background: theme.bgElevated, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0.6rem 0.8rem', fontSize: '0.78rem', color: '#ffa726', marginBottom: '1rem' }}>
          {open} item{open === 1 ? '' : 's'} still open, so these figures are interim — a
          median over part of a batch is not the batch's result. Note that an open item is
          still judged: an E2E passes only if BOTH legs came in under their SLA maximum, and
          an item already past its threshold is recorded as breached even while it remains
          open. What does not happen is an automatic status change — the item stays visible
          instead of being tidied away as failed.
        </div>
      )}

      {dispatchPending ? null : !stats ? (
        <p style={{ color: '#9aa0a6', fontSize: '0.85rem' }}>
          Statistics are computed every 5 seconds while a batch is active — the first snapshot
          appears shortly after launch.
        </p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(17rem, 1fr))', gap: '0.9rem', marginBottom: '1rem' }}>
            {['outbound', 'inbound', 'combined'].map(k =>
              stats.perspectives[k] ? <PerspectiveCard key={k} name={k} p={stats.perspectives[k]} /> : null
            )}
          </div>

          <div style={{ fontSize: '0.74rem', color: '#666', marginBottom: '1.2rem' }}>
            Percentiles use {stats.percentile_method}, and are shown only when the sample can
            support them (p90 needs {stats.percentile_minimum_n?.p90}, p95{' '}
            {stats.percentile_minimum_n?.p95}, p99 {stats.percentile_minimum_n?.p99}).
            {stats.fallback_excluded_from_per_leg?.count > 0 && (
              <div style={{ color: '#ffa726', marginTop: '0.3rem' }}>
                {stats.fallback_excluded_from_per_leg.count} item(s) were received without an
                outbound completion, so only a combined window exists for them. They are
                counted in combined and excluded from outbound and inbound — which is why
                those populations differ in size.
              </div>
            )}
            {/* FINISH-LINE A4 item 12, exception E1 — the quality prose block (scored/mean/worst,
                the sla_component sentence, the breach count and its denominator, the
                sla_pass_pct effect and the capitalised comparability warning) is removed
                OUTRIGHT from this surface and deliberately NOT relocated to Help: every batch
                in this database is test data and nobody reading this app used an earlier
                version, so an app-history lesson here is noise.

                NO POLICY CHANGED. In backend/services/batch_stats: quality is still counted
                only over the items whose job configured a minimum (`quality_enabled`), a
                quality breach still fails ITS ITEM outright through the same conjunction as the
                legs (`_item_met_sla`), it gets no weighting of its own, and it contributes no
                DURATION to the combined budget. The snapshot fields are still computed and
                still served; the emailed report and the Slack card still render them. Per-item
                quality remains visible in the Items table below. */}
          </div>
        </>
      )}

      {/* Suppressed while dispatch is pending: a header row over zero items is the
          "empty-looking report" G1.4 exists to prevent, and the panel above already
          says what is happening. items.length is 0 here by construction. */}
      {!dispatchPending && (
        <>
          <h3 style={{ fontSize: '0.95rem', margin: '0 0 0.5rem' }}>Items</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: theme.bgElevated, textAlign: 'left' }}>
                <th style={{ padding: '0.45rem' }}>#</th>
                <th style={{ padding: '0.45rem' }}>Run ID</th>
                <th style={{ padding: '0.45rem' }}>Status</th>
                <th style={{ padding: '0.45rem' }}>Outbound</th>
                <th style={{ padding: '0.45rem' }}>Inbound</th>
                <th style={{ padding: '0.45rem' }}>Combined</th>
                <th style={{ padding: '0.45rem' }}>Quality</th>
                {/* The operator checks a fax's live status from THIS screen rather than opening
                    each item. Same affordance the dashboard row has, same component. */}
                <th style={{ padding: '0.45rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id} style={{ borderTop: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '0.45rem', color: '#9aa0a6' }}>{i.batch_seq}</td>
                  <td style={{ padding: '0.45rem', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                    {/* Links to a BATCH ITEM detail page, not /runs/{id}: a batch item is not in
                        the runs table, so the run route would 404. */}
                    <Link to={`/tactical/${id}/items/${i.id}`} style={{ color: theme.primary }}>
                      {i.run_token}
                    </Link>
                  </td>
                  <td style={{ padding: '0.45rem' }}>
                    {i.status}
                    {/* Either channel — the dispatcher's own error, or the shared send path's,
                        which records into quality_detail. Showing one field left a send_failed
                        item with no visible reason. */}
                    {(i.send_error || i.quality_detail) && (
                      <div style={{ color: '#ef9a9a', fontSize: '0.7rem' }}>
                        {i.send_error || i.quality_detail}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.45rem', color: i.outbound_sla_breached ? '#f44336' : undefined }}>
                    {secs(i.outbound_seconds)}
                  </td>
                  <td style={{ padding: '0.45rem', color: i.inbound_sla_breached ? '#f44336' : undefined }}>
                    {secs(i.inbound_seconds)}
                  </td>
                  <td style={{ padding: '0.45rem' }}>{secs(i.combined_seconds)}</td>
                  <td style={{ padding: '0.45rem' }}>
                    {i.quality_score !== null && i.quality_score !== undefined
                      ? i.quality_score.toFixed(1)
                      : <span style={{ color: '#666' }}>{i.quality_result || '—'}</span>}
                  </td>
                  <td style={{ padding: '0.45rem' }}>
                    {/* THE SAME component the dashboard row renders, not a second copy. The only
                        argument is this item's own id: `GET /api/transmissions/{id}/outbound-info`
                        resolves both tables and resolves THIS leg's provider, so an SFax item reads
                        SFax's record under SFax's own labels. Passing a provider from here is what
                        put an SFax SendFaxQueueId under the heading "eFax fax_id", so there is no
                        parameter for one. `load` is handed over so an eFax refresh that writes new
                        timestamps is reflected without waiting for the 5s poll. */}
                    <OutboundInfoButton
                      transmissionId={i.id}
                      onRefreshed={load}
                      title="Show this fax's outbound record: the provider and transport that carried it, its provider-side handle, and why anything is missing"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
